import Raven from 'raven-js';
import { useEffect, useMemo, useState } from 'react';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import ErrorBoundary from '../ErrorBoundary';
import { USAGE_LIMIT_BANNER } from '../_core/common/constants/PurchaseMotionLocations';
import LimitPackBanner from './banner/LimitPackBanner';
import LoadingBanner from './banner/LoadingBanner';
import RemoveBrandingBanner from './banner/RemoveBrandingBanner';
import UsageLimitBanner from './banner/UsageLimitBanner';
import { POINT_TYPES, UPGRADE_BANNER_TYPES } from './types';
import { UpgradePointStatus, getUpgradeDataTrackingProperties, updateViewedUpgradePointsCache, warnArchivedUpgradePointUse } from './utils';

// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'self... Remove this comment to see the full error message
import { getLocationAwareProductPurchaseMotionData } from 'self-service-api/api/getProductPurchaseMotionData';
import { fetchValidatedBannerDetails } from './fetchValidatedBannerDetails';
import { useGates } from '../_core/common/hooks/useGates';
import { jsx as _jsx } from "react/jsx-runtime";
const UIUpgradeBanner = ({
  upgradeKey,
  className,
  limit,
  value,
  displayThreshold,
  style = {},
  'data-test-id': dataTestId
}) => {
  const [fetchedBannerDetails, setFetchedBannerDetails] = useState({
    loading: true,
    error: null
  });
  const [purchaseMotionResponse, setPurchaseMotionResponse] = useState();
  const gates = useGates();
  const isValueBelowDisplayThreshold = useMemo(() => {
    return displayThreshold && value !== undefined && value >= 0 && value < displayThreshold;
  }, [displayThreshold, value]);
  useEffect(() => {
    fetchValidatedBannerDetails(upgradeKey).then(({
      upgradeConfig,
      isFreePortal,
      ownedProducts
    }) => {
      if (!upgradeConfig) return;
      if (!Object.keys(UPGRADE_BANNER_TYPES).includes(upgradeConfig.pointType)) {
        throw new Error(`(UIUpgradeBanner) - ${upgradeKey} was declared with an incompatible point type "${upgradeConfig.pointType}"`);
      }
      setFetchedBannerDetails({
        // usage limit banners still need to fetch PMB so stay in a loading state
        loading: upgradeConfig.pointType === USAGE_LIMIT_BANNER,
        error: null,
        data: {
          upgradeConfig,
          isFreePortal,
          ownedProducts
        }
      });
      updateViewedUpgradePointsCache(upgradeKey, () => {
        if (upgradeConfig.status === UpgradePointStatus.ARCHIVED) {
          warnArchivedUpgradePointUse(upgradeConfig.upgradeKey);
        }
        let limitInteractionTrackingData = {};
        if ((upgradeConfig.pointType === POINT_TYPES.USAGE_LIMIT_BANNER || upgradeConfig.pointType === POINT_TYPES.LIMIT_PACK_BANNER) && limit && value !== undefined && value >= 0) {
          const hasReachedLimit = value >= limit;
          limitInteractionTrackingData = {
            limit,
            value,
            displayThreshold,
            hasReachedLimit
          };
        }
        if (!isValueBelowDisplayThreshold) {
          track('interaction', Object.assign({
            action: 'viewed upgrade point'
          }, limitInteractionTrackingData, getUpgradeDataTrackingProperties(upgradeConfig, true, true)));
        }
      });
    }).catch(error => {
      Raven.captureException(error, {
        extra: {
          message: 'Unexpected error when fetching upgrade banner data',
          upgradeKey
        }
      });
      setFetchedBannerDetails({
        loading: false,
        error
      });
    });
  }, [upgradeKey, limit, value, displayThreshold, isValueBelowDisplayThreshold]);

  // Fetch purchase motions for usage limit banners only
  useEffect(() => {
    if (fetchedBannerDetails.data && fetchedBannerDetails.data.upgradeConfig && fetchedBannerDetails.data.upgradeConfig.pointType === POINT_TYPES.USAGE_LIMIT_BANNER) {
      getLocationAwareProductPurchaseMotionData({
        locations: [USAGE_LIMIT_BANNER]
      }).then(response => {
        if (!response) {
          throw new Error('Response is empty');
        }
        setPurchaseMotionResponse(response);
        setFetchedBannerDetails(previousState => Object.assign({}, previousState, {
          loading: false,
          error: null
        }));
      }).catch(error => {
        if (error && error.status !== 0) {
          Raven.captureException(new Error('Unexpected error processing purchase-motions response'), {
            extra: {
              error
            }
          });
        }
        setPurchaseMotionResponse(null);
        setFetchedBannerDetails({
          loading: false,
          error
        });
      });
    }
  }, [fetchedBannerDetails.data]);
  if (isValueBelowDisplayThreshold) {
    return null;
  }
  if (fetchedBannerDetails.loading || !gates) {
    return /*#__PURE__*/_jsx(LoadingBanner, {
      className: className,
      style: style
    });
  }
  if (fetchedBannerDetails.error || !fetchedBannerDetails.data) {
    return null;
  }
  const {
    isFreePortal,
    ownedProducts,
    upgradeConfig
  } = fetchedBannerDetails.data;
  if (purchaseMotionResponse !== undefined && limit && value !== undefined && value >= 0 && upgradeConfig.pointType === POINT_TYPES.USAGE_LIMIT_BANNER) {
    return /*#__PURE__*/_jsx(UsageLimitBanner, {
      upgradePoint: upgradeConfig,
      limit: limit,
      value: value,
      hasReachedLimit: value >= limit,
      displayThreshold: displayThreshold,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      purchaseMotionData: purchaseMotionResponse,
      isFreePortal: isFreePortal,
      ownedProducts: ownedProducts,
      gates: gates
    });
  }
  if (limit && value !== undefined && value >= 0 && upgradeConfig.pointType === POINT_TYPES.LIMIT_PACK_BANNER) {
    return /*#__PURE__*/_jsx(LimitPackBanner, {
      upgradePoint: upgradeConfig,
      limit: limit,
      value: value,
      displayThreshold: displayThreshold,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      gates: gates
    });
  }
  if (upgradeConfig.pointType === POINT_TYPES.REMOVE_BRANDING_BANNER) {
    return /*#__PURE__*/_jsx(RemoveBrandingBanner, {
      upgradePoint: upgradeConfig,
      className: className,
      "data-test-id": dataTestId,
      style: style,
      isFreePortal: isFreePortal,
      gates: gates
    });
  }
  return null;
};
const UIUpgradeBannerWithErrorBoundary = props => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "UIUpgradeBanner",
    children: /*#__PURE__*/_jsx(UIUpgradeBanner, Object.assign({}, props))
  });
};
export default UIUpgradeBannerWithErrorBoundary;